/**
 * Integrations settings — tile-based UI.
 *
 * Backend registry `/api/integrations` drives the tile list, so adding a new
 * provider only requires a backend change. Each tile shows a status pill,
 * category chip, and "last tested" timestamp; clicking opens a side panel
 * with the provider's setup guide, config fields, save + test buttons.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Cloud, MessageSquare, Mail, MapPin, Package, RefreshCcw,
  CheckCircle2, AlertCircle, XCircle, HelpCircle, Zap, X, ExternalLink, Send, ChevronDown,
} from 'lucide-react';

type Status = 'connected' | 'not_configured' | 'not_connected';

interface FieldSpec {
  key: string;
  label: string;
  type: 'text' | 'password' | 'boolean';
  required?: boolean;
  secret?: boolean;
  placeholder?: string;
}

interface ProviderTile {
  key: string;
  name: string;
  category: string;
  description: string;
  fields: FieldSpec[];
  config: Record<string, unknown>;
  enabled: boolean;
  configured: boolean;
  missing_fields: string[];
  last_test_status: 'success' | 'failure' | null;
  last_test_at: string | null;
  last_test_reason: string | null;
  connection_status: Status;
  updated_at: string | null;
}

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  storage: <Cloud size={20} />,
  sms: <MessageSquare size={20} />,
  email: <Mail size={20} />,
  geocoding: <MapPin size={20} />,
  push: <Zap size={20} />,
};

const STATUS_META: Record<Status, { label: string; badge: 'success' | 'warning' | 'muted'; icon: React.ReactNode }> = {
  connected: { label: 'Connected', badge: 'success', icon: <CheckCircle2 size={12} /> },
  not_configured: { label: 'Not Configured', badge: 'muted', icon: <HelpCircle size={12} /> },
  not_connected: { label: 'Not Connected', badge: 'warning', icon: <AlertCircle size={12} /> },
};

// Safe accessor: any legacy status coming from the API defaults to Not Connected.
const statusMeta = (s: Status | string | null | undefined): typeof STATUS_META[Status] =>
  STATUS_META[(s as Status) in STATUS_META ? (s as Status) : 'not_connected'];

// Provider-specific step-by-step setup guides embedded in the config panel.
const SETUP_GUIDES: Record<string, { title: string; steps: React.ReactNode[]; docs_url?: string }> = {
  onedrive: {
    title: 'Connecting OneDrive for Business (app-only)',
    docs_url: 'https://learn.microsoft.com/en-us/graph/auth-v2-service',
    steps: [
      <><strong>Pick (or create) a dedicated Microsoft 365 user.</strong> RAPTOR needs a licensed M365 account whose OneDrive for Business will hold every uploaded file. We strongly recommend creating a shared service account (for example <code className="text-xs bg-raptor-neutral/40 px-1 rounded">raptor-storage@yourcompany.co.za</code>) so files survive staff turnover. <span className="text-red-600 font-semibold">Sign into that account at least once at <a className="text-raptor-blue underline" href="https://onedrive.live.com" target="_blank" rel="noreferrer">onedrive.live.com</a> to provision the drive — Microsoft only creates a personal OneDrive after the first interactive sign-in.</span></>,
      <><strong>Register an application in Microsoft Entra ID.</strong> Sign in to the <a className="text-raptor-blue underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">Entra admin center</a> as a Global Admin and open <strong>Identity → Applications → App registrations → New registration</strong>. Name it <em>RAPTOR OneDrive Storage</em>, select <strong>Accounts in this organizational directory only</strong>, and leave the redirect URI blank. Click <strong>Register</strong>.</>,
      <><strong>Copy the two IDs.</strong> On the app's Overview page, copy the <strong>Directory (tenant) ID</strong> and the <strong>Application (client) ID</strong> and paste each into the matching field on the right. <span className="text-raptor-navy/60">Both are GUIDs — verify you don't accidentally paste the field placeholder <code className="text-[10px] bg-raptor-neutral/40 px-1 rounded">00000000-…</code>.</span></>,
      <><strong>Create a client secret.</strong> Open <strong>Certificates &amp; secrets → Client secrets → New client secret</strong>, choose an expiry (24 months is common), click <strong>Add</strong>, then immediately copy the <strong>Value</strong> column (not the Secret ID) into <strong>Client secret value</strong>. The value is only shown once — if you miss it you must create a new secret.</>,
      <><strong>Grant OneDrive access to the app.</strong> Under <strong>API permissions → Add a permission → Microsoft Graph → Application permissions</strong>, tick <code className="text-xs bg-raptor-neutral/40 px-1 rounded">Files.ReadWrite.All</code> and click <strong>Add permissions</strong>. Back on the API permissions page, click <strong>Grant admin consent for &lt;your tenant&gt;</strong> and confirm — the status column for that permission must turn green with a tick. <span className="text-raptor-navy/70 italic">(No other Graph permission is required — RAPTOR only reads/writes files.)</span></>,
      <><strong>Optional — richer diagnostics.</strong> If you also add <code className="text-xs bg-raptor-neutral/40 px-1 rounded">User.Read.All</code> (Application, with admin consent), RAPTOR's Test connection can distinguish "user does not exist" from "user has no OneDrive licence". This is nice-to-have, not required.</>,
      <><strong>Enter the OneDrive account.</strong> Paste the exact user principal name (UPN) of the M365 user from step 1 into <strong>OneDrive account</strong>. That user must be licensed for OneDrive for Business (Microsoft 365 Business Basic includes it — check <a className="text-raptor-blue underline" href="https://admin.microsoft.com" target="_blank" rel="noreferrer">Microsoft 365 admin center</a> → Users → Active users → the account → Licenses & apps).</>,
      <><strong>Choose a base folder</strong> (for example <code className="text-xs bg-raptor-neutral/40 px-1 rounded">RAPTOR/Uploads</code>). RAPTOR will create it (and any parent folders) automatically on the first upload — no need to pre-create it in OneDrive.</>,
      <><strong>Save → Test connection → Enable.</strong> If the test succeeds you'll see the drive name, web URL, and remaining quota in the result panel. Toggle <strong>Enabled</strong> — every new import PDF, survey photo, signature, and bordereau export will land in that OneDrive folder.</>,
      <><strong>Still failing after Save?</strong> Common causes: (a) admin consent hasn't propagated yet — <em>wait 2–3 minutes and retry</em>; (b) the OneDrive for the user has never been provisioned (see step 1); (c) the client secret is the <em>Secret ID</em> instead of the <em>Value</em> (they look similar in the Entra UI); (d) the tenant ID is wrong — you can double-check it under <em>Overview → Basic information → Tenant ID</em>. The <strong>Suggested fix</strong> callout under the test result will point at whichever of these is likely.</>,
    ],
  },
  microsoft_graph_mail: {
    title: 'Connecting Microsoft 365 Email (Graph SendMail)',
    docs_url: 'https://learn.microsoft.com/en-us/graph/api/user-sendmail',
    steps: [
      <><strong>Provision your sending mailbox(es).</strong> Every "From" address RAPTOR sends as must be a real licensed Microsoft 365 mailbox in your tenant. Start with one that will serve as the tenant default (for example <code className="text-xs bg-raptor-neutral/40 px-1 rounded">no-reply@ospreyunderwriting.com</code>). You can add branded mailboxes later (<em>underwriting@…</em>, <em>claims@…</em>, <em>support@…</em>) and assign them to individual templates. Each mailbox needs an Exchange Online licence (Microsoft 365 Business Basic and above include this).</>,
      <><strong>Register an app in Microsoft Entra ID.</strong> Sign in to the <a className="text-raptor-blue underline" href="https://entra.microsoft.com" target="_blank" rel="noreferrer">Entra admin center</a> as a Global Admin → <strong>Identity → Applications → App registrations → New registration</strong>. Name it <em>RAPTOR Email Sender</em>, choose <strong>Accounts in this organizational directory only</strong>, leave redirect URI blank → <strong>Register</strong>. <span className="text-raptor-navy/60">You can reuse the same app registration as OneDrive if you prefer — just add the Mail.Send permission alongside Files.ReadWrite.All.</span></>,
      <><strong>Copy the two IDs.</strong> On the Overview page, copy <strong>Directory (tenant) ID</strong> and <strong>Application (client) ID</strong> into the matching fields on the right.</>,
      <><strong>Create a client secret.</strong> <strong>Certificates &amp; secrets → Client secrets → New client secret</strong> → pick 24-month expiry → <strong>Add</strong>, then <strong>immediately</strong> copy the <strong>Value</strong> column (not Secret ID) into <strong>Client secret value</strong>. The value is only shown once; if you miss it, delete the secret and create a fresh one.</>,
      <><strong>Grant the Mail.Send permission.</strong> Under <strong>API permissions → Add a permission → Microsoft Graph → Application permissions</strong>, tick <code className="text-xs bg-raptor-neutral/40 px-1 rounded">Mail.Send</code> and click <strong>Add permissions</strong>. Then on the API permissions list, click <strong>Grant admin consent for &lt;your tenant&gt;</strong> — status must turn green. <span className="text-raptor-navy/70 italic">(Optional but recommended: also grant <code className="text-[10px] bg-raptor-neutral/40 px-1 rounded">User.Read.All</code> — Application — so Test connection can distinguish "mailbox does not exist" from "app has no send permission".)</span></>,
      <><strong>Restrict the app to only the mailboxes it may send from</strong> <span className="text-raptor-navy/60">(security best practice)</span>. Without this, the Mail.Send permission lets the app send as <em>any</em> mailbox in the tenant. To scope it down, run this from an Exchange PowerShell session as tenant admin:<br/>
      <code className="block text-xs bg-raptor-neutral/50 p-2 rounded mt-1 whitespace-pre-wrap">{`# 1. Create a mail-enabled security group that lists every allowed sender.
New-DistributionGroup -Name "RAPTOR Senders" -Type Security \\
  -PrimarySmtpAddress raptor-senders@ospreyunderwriting.com

# 2. Add each From mailbox to the group.
Add-DistributionGroupMember -Identity "RAPTOR Senders" -Member "no-reply@ospreyunderwriting.com"
Add-DistributionGroupMember -Identity "RAPTOR Senders" -Member "underwriting@ospreyunderwriting.com"

# 3. Restrict the app registration to that group only.
New-ApplicationAccessPolicy -AppId <CLIENT_ID> \\
  -PolicyScopeGroupId raptor-senders@ospreyunderwriting.com \\
  -AccessRight RestrictAccess \\
  -Description "RAPTOR — restrict Mail.Send to whitelisted mailboxes"

# 4. (Optional) Confirm a mailbox is allowed / denied:
Test-ApplicationAccessPolicy -Identity "no-reply@ospreyunderwriting.com" -AppId <CLIENT_ID>`}</code>
      <span className="text-raptor-navy/60">Reference: <a className="text-raptor-blue underline" href="https://learn.microsoft.com/en-us/graph/auth-limit-mailbox-access" target="_blank" rel="noreferrer">Limiting application permissions to specific mailboxes</a>.</span></>,
      <><strong>Fill in the connection panel.</strong> Paste <strong>Default sender mailbox</strong> (UPN of the mailbox you provisioned in step 1). Optionally set <strong>Global reply-to</strong> if you want replies to always route to a shared inbox (e.g. <em>support@…</em>). Leave <strong>Save copies to Sent Items</strong> ticked if compliance requires an audit trail in the mailbox.</>,
      <><strong>Save → Test connection.</strong> A green result confirms the token was issued, admin consent is in place, and the default sender mailbox is reachable. Any failure returns a specific hint (invalid tenant, expired secret, missing consent, or unlicensed mailbox).</>,
      <><strong>Toggle Enabled.</strong> RAPTOR switches from the developer stub logger to real Graph SendMail immediately. Legacy dispatches without a per-template "From" identity fall back to the default sender you just configured.</>,
      <><strong>Verify with a real send.</strong> After enabling, trigger any communication (OTP, cover dispatch, alert). The email must arrive from your <em>default_sender</em> address. If a template later has its own "From" identity assigned (Settings → Communications → Templates), that overrides the default per-send.</>,
      <><strong>Troubleshooting quick reference:</strong>
      <ul className="list-disc pl-5 mt-1 space-y-1 text-raptor-navy/70">
        <li><em>ErrorAccessDenied</em> — Mail.Send permission missing or admin consent not granted; also verify the mailbox is in the RAPTOR Senders group if you scoped access.</li>
        <li><em>Mailbox not found</em> — UPN typo, or the mailbox has no Exchange Online licence.</li>
        <li><em>InvalidAuthenticationToken</em> — client secret expired; create a new one and re-save.</li>
        <li><em>Message sent but marked as spam</em> — configure SPF + DKIM for the sending domain in your DNS. Microsoft's official guide: <a className="text-raptor-blue underline" href="https://learn.microsoft.com/en-us/microsoft-365/security/office-365-security/email-authentication-about" target="_blank" rel="noreferrer">Email authentication in Microsoft 365</a>.</li>
      </ul></>,
    ],
  },
  panacea: {
    title: 'Connecting Panacea Mobile (SMS)',
    docs_url: 'https://panaceamobile.gitbook.io/panacea-mobile-help-centre/',
    steps: [
      <>Sign in to your <a className="text-raptor-blue underline" href="https://panaceamobile.com" target="_blank" rel="noreferrer">Panacea Mobile</a> account. Under <strong>Account → API access</strong>, note the <strong>Username</strong> and <strong>Password</strong> configured for API use (these are separate from your web-portal login).</>,
      <>Confirm your <strong>Sender ID</strong> is provisioned. In South Africa most alphanumeric IDs need to be pre-registered with your Panacea account manager. A safe default is <code className="text-xs bg-raptor-neutral/40 px-1 rounded">RAPTOR</code>.</>,
      <>Leave <strong>API endpoint</strong> as the default (<code className="text-xs bg-raptor-neutral/40 px-1 rounded">https://eu-api.panaceamobile.com/json</code>) unless Panacea Support have given you a different one for your region.</>,
      <>Set <strong>Default country code</strong> to <code className="text-xs bg-raptor-neutral/40 px-1 rounded">27</code> for South Africa. RAPTOR normalises any leading zero automatically (e.g. <em>0821234567 → 27821234567</em>).</>,
      <>Save the configuration, then click <strong>Test connection</strong>. A successful test proves the credentials are accepted by the gateway; the raw JSON response is shown for reference.</>,
      <>Toggle <strong>Enabled</strong>. OTPs (broker onboarding, client registration) and platform alerts will now route through Panacea. Turning it off any time falls the platform back to the developer stub logger.</>,
    ],
  },
  google_maps: {
    title: 'Connecting Google Maps (Places + Geocoding + Street View)',
    docs_url: 'https://developers.google.com/maps/documentation',
    steps: [
      <>Sign in to the <a className="text-raptor-blue underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">Google Cloud Console</a> as a project owner. Create a new project called <em>RAPTOR Maps</em> (or reuse an existing one).</>,
      <>Enable billing for the project — Google requires a credit card even for the free tier. Under <strong>Billing → Manage billing accounts</strong>, link a billing account to the project. Every Maps SKU currently ships with <strong>US$200/month free credit</strong>, which comfortably covers a small underwriting portfolio (roughly 40 000 autocomplete sessions + 40 000 geocodes + 28 000 static map / Street View tiles per month).</>,
      <>Enable the required APIs. Go to <strong>APIs &amp; Services → Library</strong> and click <strong>Enable</strong> on each of:
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li><strong>Maps JavaScript API</strong> — powers the address autocomplete widget across Sites, Site Detail, Client Detail, Brokers and Broker Detail.</li>
          <li><strong>Places API</strong> — supplies address suggestions and place details (required by the Autocomplete widget).</li>
          <li><strong>Geocoding API</strong> — server-side lat/lng resolution used by the analytics backfill + risk engine.</li>
          <li><strong>Street View Static API</strong> — Street View pane in the Analytics site drill-in modal.</li>
          <li><em>Optional:</em> <strong>Maps Static API</strong> — enables future PDF report thumbnails.</li>
          <li><em>Enable when the native app ships:</em> <strong>Maps SDK for Android</strong>, <strong>Places SDK for Android</strong>, <strong>Maps SDK for iOS</strong>, <strong>Places SDK for iOS</strong>.</li>
        </ul>
      </>,
      <><strong>Create four API keys — one per platform</strong>. Google explicitly forbids mixing restriction types on a single key (you can't do "HTTP referrers AND Android package" on one key), so best practice is one key per platform. Under <strong>APIs &amp; Services → Credentials → Create credentials → API key</strong> repeat four times:
        <ul className="list-disc pl-5 mt-1 space-y-1">
          <li><strong>1. Server key</strong> — <em>Application restrictions</em>: <strong>IP addresses</strong> → add your Raptor backend egress IP. <em>API restrictions</em>: <strong>Geocoding API</strong>. This key never leaves the backend.</li>
          <li><strong>2. Browser key</strong> — <em>Application restrictions</em>: <strong>HTTP referrers (websites)</strong> → add <code className="text-xs bg-raptor-neutral/40 px-1 rounded">https://*.ospreyunderwriting.com/*</code> and <code className="text-xs bg-raptor-neutral/40 px-1 rounded">https://wysiwyg-insurance.preview.emergentagent.com/*</code>. <em>API restrictions</em>: <strong>Maps JavaScript API</strong>, <strong>Places API</strong> and <strong>Street View Static API</strong>. This key is safe to expose to the browser because Google enforces the referrer check.</li>
          <li><strong>3. Android key</strong> <em>(create now, use later)</em> — <em>Application restrictions</em>: <strong>Android apps</strong> → add the package name (e.g. <code className="text-xs bg-raptor-neutral/40 px-1 rounded">com.ospreyunderwriting.raptor</code>) and both the <em>debug</em> and <em>release</em> SHA-1 fingerprints. <em>API restrictions</em>: <strong>Maps SDK for Android</strong> + <strong>Places SDK for Android</strong>. Paste into the Android key field on the right — leave blank if you're not building the native app yet.</li>
          <li><strong>4. iOS key</strong> <em>(create now, use later)</em> — <em>Application restrictions</em>: <strong>iOS apps</strong> → add the bundle ID (e.g. <code className="text-xs bg-raptor-neutral/40 px-1 rounded">com.ospreyunderwriting.raptor</code>). <em>API restrictions</em>: <strong>Maps SDK for iOS</strong> + <strong>Places SDK for iOS</strong>. Paste into the iOS key field on the right — leave blank if you're not building the native app yet.</li>
        </ul>
        Each key is independently revocable — if your Android SHA-1 ever leaks, you rotate just the Android key without disrupting the web app.</>,
      <><strong>Enable the mobile SDKs too</strong> so the Android + iOS keys work the moment your native app ships. Back in <strong>APIs &amp; Services → Library</strong>, also enable:
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li><strong>Maps SDK for Android</strong> + <strong>Places SDK for Android</strong></li>
          <li><strong>Maps SDK for iOS</strong> + <strong>Places SDK for iOS</strong></li>
        </ul>
        Skipping this until the mobile build starts is fine — Google won't charge you for enabled-but-unused APIs.</>,
      <>Set <strong>Region bias</strong> to <code className="text-xs bg-raptor-neutral/40 px-1 rounded">za</code> so ambiguous queries (e.g. <em>"Sandton"</em>) surface South African places first. The autocomplete widget also filters to this country by default.</>,
      <>Click <strong>Save → Test connection → Enable</strong>. The test probe geocodes <em>Sandton City, Johannesburg</em> using the <b>server key</b>. On success you'll see the resolved coordinates. <em>Note: the test only validates the server key — the browser / Android / iOS keys are validated implicitly the first time each platform uses Autocomplete or a Map.</em></>,
      <><strong>Verify autocomplete is live</strong>: hard-refresh (Ctrl/Cmd + Shift + R) any Raptor tab, then go to <em>Clients &amp; Sites → New site</em>. Start typing an address — you should see Google's dropdown appear within one keystroke, and picking a suggestion auto-fills city / province / postal / lat / lng. If nothing happens, open DevTools → Console and look for messages beginning with <code className="text-xs bg-raptor-neutral/40 px-1 rounded">Google Maps JavaScript API error:</code>. The three usual suspects are: (a) referrer restriction doesn't include your current domain, (b) Places API isn't enabled on this project, (c) billing isn't linked to the project.</>,
      <><strong>Verify Street View is live</strong>: go to <em>Insurer → Analytics</em> and click any marker on the map. The site insight modal opens with two panes side-by-side — the right pane should show a Street View image (or a "no imagery for these coords" message if the site is inside a building complex Google hasn't photographed).</>,
      <><strong>Configure geofence thresholds</strong> in <em>Settings → Survey Geolocation</em>: set the global soft-block distance (default 200 m), the hard cap (default 2 000 m), and whether surveyors must supply a reason when bypassing. Per-site overrides live on the <em>Site edit</em> form for anything that needs a bigger or tighter radius than the global default.</>,
    ],
  },
  mapbox: {
    title: 'Connecting Mapbox Geocoding',
    docs_url: 'https://docs.mapbox.com/api/search/geocoding-v6/',
    steps: [
      <>Sign up for a free <a className="text-raptor-blue underline" href="https://account.mapbox.com/auth/signup/" target="_blank" rel="noreferrer">Mapbox account</a>. The free tier includes 100 000 geocoding requests per month, no credit card required.</>,
      <>Open <strong><a className="text-raptor-blue underline" href="https://account.mapbox.com/access-tokens/" target="_blank" rel="noreferrer">Account → Access tokens</a></strong>. Either use the auto-created <em>Default public token</em> or click <strong>Create a token</strong> to make one dedicated to RAPTOR.</>,
      <>If you create a dedicated token, select scopes <code className="text-xs bg-raptor-neutral/40 px-1 rounded">geocoding:read</code> is included in every public token by default. For extra safety on server-side use, create a <em>secret token</em> (prefixed <code className="text-xs">sk.</code>) restricted to the geocoding scopes.</>,
      <>Copy the token value into the <strong>Access token</strong> field on the right.</>,
      <>Leave <strong>Country restriction</strong> as <code className="text-xs bg-raptor-neutral/40 px-1 rounded">za</code> so results are constrained to South Africa. Multiple ISO-2 codes are supported (comma-separated) if you operate cross-border.</>,
      <>Click <strong>Save → Test connection → Enable</strong>. The test probe geocodes <em>Sandton City, Johannesburg</em>. Mapbox's token includes rate-limit quota visible on your account dashboard.</>,
    ],
  },
  nominatim: {
    title: 'Connecting OpenStreetMap Nominatim',
    docs_url: 'https://operations.osmfoundation.org/policies/nominatim/',
    steps: [
      <>Read the <a className="text-raptor-blue underline" href="https://operations.osmfoundation.org/policies/nominatim/" target="_blank" rel="noreferrer">Nominatim usage policy</a>. The public endpoint is free but capped at ~1 request per second and requires a descriptive User-Agent header identifying your application.</>,
      <>Choose a <strong>User-Agent</strong> string. It must be specific enough to identify RAPTOR — e.g. <code className="text-xs bg-raptor-neutral/40 px-1 rounded">RAPTOR/1.0 (osprey-underwriting)</code>. Generic strings like <code className="text-xs">curl/8</code> will get you blocked.</>,
      <>Add a <strong>Contact email</strong> — Nominatim's admins may contact you before blocking abusive traffic. Use an ops mailbox that's actively monitored.</>,
      <>Leave the <strong>Endpoint</strong> blank to use the community public server. If you self-host Nominatim (recommended for high volume), put your instance URL here.</>,
      <>Leave <strong>Country codes</strong> as <code className="text-xs bg-raptor-neutral/40 px-1 rounded">za</code> to bias results to South African addresses.</>,
      <>Click <strong>Save → Test connection → Enable</strong>. Note: Nominatim is best used as a <em>fallback</em>, not a primary geocoder — cache every response aggressively and never batch-geocode more than a few thousand addresses without self-hosting.</>,
    ],
  },
};

const Toggle: React.FC<{ checked: boolean; onCheckedChange: (v: boolean) => void; disabled?: boolean; testId?: string }> = ({ checked, onCheckedChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-raptor-blue/40 ${
      checked ? 'bg-raptor-blue' : 'bg-raptor-neutral'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const providerIconFor = (category: string): React.ReactNode => CATEGORY_ICON[category] ?? <Package size={20} />;

export const IntegrationsSettings: React.FC = () => {
  const [providers, setProviders] = useState<ProviderTile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selected, setSelected] = useState<ProviderTile | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [secretHints, setSecretHints] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [testing, setTesting] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<Record<string, unknown> | null>(null);
  // Setup guides can be long (Google Maps ships 9 steps) — the config
  // form's Save/Test buttons would end up below the fold. Collapsing
  // the guide by default keeps the actionable controls in view; we
  // auto-expand it for providers that aren't yet configured so
  // first-time setup still surfaces the walkthrough.
  const [guideOpen, setGuideOpen] = useState<boolean>(false);
  const { user } = useAuth();

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const r = await api.get<{ providers: ProviderTile[] }>('/integrations');
      setProviders(r.data.providers);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load integrations.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Mount-once load — see comment above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openConfig = (p: ProviderTile): void => {
    setSelected(p);
    setLastResult(null);
    // Split incoming config into form values (editable) and secret hints
    // (displayed as a caption). Secret fields render EMPTY inputs so nothing
    // sensitive can be accidentally re-transmitted or visually leaked, while
    // the caption still tells the operator whether a secret is saved and
    // shows its last 4 characters for visual verification.
    const initial: Record<string, unknown> = { enabled: p.enabled };
    const hints: Record<string, string> = {};
    for (const f of p.fields) {
      let v = p.config[f.key];
      if (f.type === 'boolean') { initial[f.key] = Boolean(v); continue; }
      if (f.secret) {
        // Backend returns either '••••••••' (short) or '••••••…••••abcd'
        // (long) for saved secrets. Preserve just as a hint; input stays blank.
        if (typeof v === 'string' && v.length > 0 && v.includes('•')) {
          hints[f.key] = v;
        }
        initial[f.key] = '';
        continue;
      }
      initial[f.key] = v ?? '';
    }
    setFormValues(initial);
    setSecretHints(hints);
    // First-time setup UX: expand the walkthrough automatically for
    // providers that aren't fully configured yet. For already-connected
    // providers we keep it collapsed so Save/Test are one glance away.
    setGuideOpen(p.connection_status !== 'connected');
  };

  const closeConfig = (): void => { setSelected(null); setFormValues({}); setSecretHints({}); setLastResult(null); };

  const setField = (key: string, value: unknown): void => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const refreshAll = async (): Promise<ProviderTile | undefined> => {
    // Single source of truth: one fetch that updates BOTH the tile grid and
    // the currently-open panel. Avoids a race where the two states could
    // otherwise disagree if a request re-orders.
    const r = await api.get<{ providers: ProviderTile[] }>('/integrations');
    setProviders(r.data.providers);
    if (selected) {
      const refreshed = r.data.providers.find((p) => p.key === selected.key);
      if (refreshed) setSelected(refreshed);
      return refreshed;
    }
    return undefined;
  };

  const saveConfig = async (): Promise<void> => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/integrations/${selected.key}`, formValues);
      toast.success(`${selected.name} configuration saved.`);
      const refreshed = await refreshAll();
      // Recompute the "Saved secret" caption + reset blank secret inputs from
      // the freshly-masked config, so the panel reflects what was just saved
      // instead of the stale pre-save "No secret saved yet." hint.
      if (refreshed) openConfig(refreshed);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (): Promise<void> => {
    if (!selected) return;
    setTesting(true);
    try {
      const r = await api.post<{ ok: boolean; status: Status; result: Record<string, unknown> }>(`/integrations/${selected.key}/test`, {});
      setLastResult(r.data.result);
      if (r.data.ok) toast.success(`${selected.name} — connection OK.`);
      else toast.error(`${selected.name} — ${(r.data.result?.reason as string) ?? 'connection failed.'}`);
      await refreshAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Test failed.';
      toast.error(msg);
      setLastResult({ error: msg });
    } finally {
      setTesting(false);
    }
  };

  const toggleEnabled = async (next: boolean): Promise<void> => {
    if (!selected) return;
    try {
      await api.post(`/integrations/${selected.key}/toggle`, { enabled: next });
      setField('enabled', next);
      toast.success(`${selected.name} ${next ? 'enabled' : 'disabled'}.`);
      await refreshAll();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Toggle failed.';
      toast.error(msg);
    }
  };

  const resetConfig = async (): Promise<void> => {
    if (!selected) return;
    if (!window.confirm(`Reset ${selected.name} configuration? This clears all saved fields (including secrets) and returns the tile to 'Not configured'. You will need to re-enter your credentials.`)) return;
    try {
      await api.delete(`/integrations/${selected.key}`);
      toast.success(`${selected.name} configuration cleared.`);
      const refreshed = await refreshAll();
      if (refreshed) openConfig(refreshed);
      setLastResult(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Reset failed.';
      toast.error(msg);
    }
  };

  const guide = useMemo(() => selected ? SETUP_GUIDES[selected.key] : null, [selected]);

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-headline text-2xl text-raptor-navy tracking-wide">Integrations</h3>
          <p className="text-sm text-raptor-navy/60 font-body">Connect RAPTOR to your storage, SMS, and messaging providers. Each tile is independently configured, tested, and toggleable.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="integrations-refresh-btn">
          <RefreshCcw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </Button>
      </div>

      {!loading && providers.length > 0 && (() => {
        const connected = providers.filter((p) => p.connection_status === 'connected').length;
        const notConnected = providers.filter((p) => p.connection_status === 'not_connected').length;
        const notConfigured = providers.filter((p) => p.connection_status === 'not_configured').length;
        const enabledCount = providers.filter((p) => p.enabled).length;
        const total = providers.length;
        const healthPct = total ? Math.round((connected / total) * 100) : 0;
        return (
          <Card data-testid="integration-health-summary" className="border-raptor-blue/20">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="font-headline text-sm text-raptor-navy tracking-wide uppercase">This tenant's integration health</div>
                  <p className="text-xs text-raptor-navy/60 font-body mt-0.5">
                    {connected} of {total} providers connected · {enabledCount} enabled. Each insurer manages its own keys — nothing is shared across tenants.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="success" className="flex items-center gap-1" data-testid="health-connected-count"><CheckCircle2 size={12} /> {connected} Connected</Badge>
                  <Badge variant="warning" className="flex items-center gap-1" data-testid="health-notconnected-count"><AlertCircle size={12} /> {notConnected} Not connected</Badge>
                  <Badge variant="muted" className="flex items-center gap-1" data-testid="health-notconfigured-count"><HelpCircle size={12} /> {notConfigured} Not configured</Badge>
                </div>
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-raptor-neutral/40 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${healthPct}%` }} />
              </div>
            </CardContent>
          </Card>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}><CardContent className="p-4"><div className="animate-pulse h-24 bg-raptor-neutral/40 rounded" /></CardContent></Card>
        ))}
        {!loading && providers.map((p) => {
          const status = statusMeta(p.connection_status);
          return (
            <button
              key={p.key}
              onClick={() => openConfig(p)}
              className="text-left group"
              data-testid={`integration-tile-${p.key}`}
            >
              <Card className="hover:border-raptor-blue/50 hover:shadow-md transition-all cursor-pointer h-full">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-md bg-raptor-blue/10 text-raptor-blue flex items-center justify-center flex-shrink-0">
                      {providerIconFor(p.category)}
                    </div>
                    <Badge variant={status.badge} className="flex items-center gap-1" data-testid={`integration-status-${p.key}`}>
                      {status.icon}{status.label}
                    </Badge>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center gap-2">
                      <div className="font-headline text-base text-raptor-navy tracking-wide">{p.name}</div>
                      {p.enabled && <Badge variant="blue" className="text-[9px]">ENABLED</Badge>}
                    </div>
                    <div className="text-[11px] uppercase text-raptor-navy/50 font-body mt-0.5">{p.category}</div>
                  </div>
                  <p className="text-xs text-raptor-navy/70 font-body mt-2 line-clamp-3">{p.description}</p>
                  <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] text-raptor-navy/60 font-tabular">
                    <span>{p.last_test_at ? `Tested ${new Date(p.last_test_at).toLocaleString()}` : 'Not yet tested'}</span>
                    <span className="text-raptor-blue opacity-0 group-hover:opacity-100 transition-opacity">Configure →</span>
                  </div>
                </CardContent>
              </Card>
            </button>
          );
        })}
        {!loading && providers.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardContent className="p-8 text-center text-raptor-navy/60 text-sm">No integrations available.</CardContent>
          </Card>
        )}
      </div>

      {/* Config side panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="integration-config-panel">
          <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={closeConfig} />
          <div className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border/70 p-4 flex items-start justify-between z-10">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-raptor-blue/10 text-raptor-blue flex items-center justify-center">
                  {providerIconFor(selected.category)}
                </div>
                <div>
                  <div className="font-headline text-lg text-raptor-navy tracking-wide">{selected.name}</div>
                  <div className="text-xs text-raptor-navy/60 font-body">{selected.description}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={statusMeta(selected.connection_status).badge} className="flex items-center gap-1">
                  {statusMeta(selected.connection_status).icon}{statusMeta(selected.connection_status).label}
                </Badge>
                <Button size="icon" variant="ghost" onClick={closeConfig} data-testid="integration-config-close">
                  <X size={18} />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Setup guide — collapsible so the Save/Test buttons in
                  the config card below stay near the fold, especially
                  for long guides like Google Maps (9 steps). Header is
                  a button; the whole card expands/collapses in place. */}
              {guide && (
                <Card className="bg-raptor-neutral/20">
                  <button
                    type="button"
                    onClick={() => setGuideOpen((v) => !v)}
                    className="w-full text-left"
                    aria-expanded={guideOpen}
                    aria-controls="integration-guide-body"
                    data-testid="integration-guide-toggle"
                  >
                    <CardHeader className="cursor-pointer select-none hover:bg-raptor-neutral/40 transition-colors rounded-t-md">
                      <CardTitle className="text-base flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2">
                          <HelpCircle size={16} /> {guide.title}
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="text-[10px] font-body font-normal text-raptor-navy/50 uppercase tracking-wide">
                            {guideOpen ? 'Hide' : `Show setup guide (${guide.steps.length} steps)`}
                          </span>
                          <ChevronDown
                            size={16}
                            className={`text-raptor-navy/60 transition-transform ${guideOpen ? 'rotate-180' : ''}`}
                          />
                        </span>
                      </CardTitle>
                      {guideOpen && guide.docs_url && (
                        <CardDescription>
                          <a
                            href={guide.docs_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-raptor-blue underline text-xs inline-flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Official documentation <ExternalLink size={11} />
                          </a>
                        </CardDescription>
                      )}
                    </CardHeader>
                  </button>
                  {guideOpen && (
                    <CardContent id="integration-guide-body">
                      <ol className="space-y-2 text-sm font-body text-raptor-navy/80 list-decimal list-inside">
                        {guide.steps.map((s, i) => (
                          <li key={i} className="leading-relaxed"><span className="ml-1">{s}</span></li>
                        ))}
                      </ol>
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Config form */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Configuration</CardTitle>
                  <CardDescription>Fields marked required must be filled before enabling.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {selected.fields.map((f) => (
                    <div key={f.key}>
                      <Label>{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                      {f.type === 'boolean' ? (
                        <div className="mt-1 flex items-center gap-2">
                          <Toggle
                            checked={Boolean(formValues[f.key])}
                            onCheckedChange={(v: boolean) => setField(f.key, v)}
                            testId={`integration-field-${selected.key}-${f.key}`}
                          />
                          <span className="text-xs text-raptor-navy/60">{Boolean(formValues[f.key]) ? 'On' : 'Off'}</span>
                        </div>
                      ) : (
                        <Input
                          type={f.type === 'password' ? 'password' : 'text'}
                          value={String(formValues[f.key] ?? '')}
                          onChange={(e) => setField(f.key, e.target.value)}
                          placeholder={f.secret && secretHints[f.key] ? 'Leave blank to keep the saved secret' : f.placeholder}
                          data-testid={`integration-field-${selected.key}-${f.key}`}
                        />
                      )}
                      {f.secret && secretHints[f.key] && (
                        <div className="text-[11px] text-raptor-navy/60 mt-1 flex items-center gap-1" data-testid={`integration-secret-hint-${selected.key}-${f.key}`}>
                          <span className="text-emerald-600">●</span>
                          Saved secret: <code className="font-tabular text-raptor-navy/80 bg-raptor-neutral/40 px-1 rounded">{secretHints[f.key]}</code>
                          <span className="text-raptor-navy/50">— retype the field to replace it.</span>
                        </div>
                      )}
                      {f.secret && !secretHints[f.key] && (
                        <div className="text-[11px] text-raptor-navy/50 mt-1">No secret saved yet.</div>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-3 border-t border-border/60">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-body text-raptor-navy">Enabled</span>
                      <Toggle
                        checked={Boolean(formValues.enabled)}
                        onCheckedChange={(v: boolean) => selected.configured ? toggleEnabled(v) : setField('enabled', v)}
                        disabled={!selected.configured}
                        testId="integration-enabled-switch"
                      />
                      {!selected.configured && <span className="text-[11px] text-raptor-navy/50">Complete required fields first</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={resetConfig} data-testid="integration-reset-btn" title="Wipe saved config so you can start fresh">
                        Reset
                      </Button>
                      <Button variant="outline" size="sm" onClick={testConnection} disabled={testing || !selected.configured} data-testid="integration-test-btn">
                        <Zap size={14} className={testing ? 'animate-pulse' : ''} /> Test connection
                      </Button>
                      <Button variant="primary" size="sm" onClick={saveConfig} disabled={saving} data-testid="integration-save-btn">
                        Save
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Last test result */}
              {(lastResult || selected.last_test_at) && (
                <Card className={selected.last_test_status === 'success' ? 'border-emerald-200' : selected.last_test_status === 'failure' ? 'border-red-200' : ''}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      {selected.last_test_status === 'success' ? <CheckCircle2 size={16} className="text-emerald-600" /> :
                       selected.last_test_status === 'failure' ? <XCircle size={16} className="text-red-600" /> :
                       <AlertCircle size={16} className="text-raptor-navy/60" />}
                      Last test result
                    </CardTitle>
                    <CardDescription>
                      {selected.last_test_at && <>Tested {new Date(selected.last_test_at).toLocaleString()}</>}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {selected.last_test_reason && (
                      <div className="text-sm text-raptor-navy/80 mb-2 whitespace-pre-wrap" data-testid="integration-test-reason">
                        {selected.last_test_reason}
                      </div>
                    )}
                    {lastResult && typeof (lastResult as Record<string, unknown>).hint === 'string' && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 mb-2 text-sm text-amber-900 flex items-start gap-2" data-testid="integration-test-hint">
                        <AlertCircle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                        <div>
                          <div className="font-semibold text-amber-900 mb-0.5">Suggested fix</div>
                          <div>{String((lastResult as Record<string, unknown>).hint)}</div>
                        </div>
                      </div>
                    )}
                    {lastResult && (
                      <details>
                        <summary className="text-xs text-raptor-navy/60 cursor-pointer">Raw response</summary>
                        <pre className="text-[11px] font-tabular bg-raptor-neutral/30 rounded p-3 overflow-x-auto whitespace-pre-wrap mt-1">{JSON.stringify(lastResult, null, 2)}</pre>
                      </details>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Panacea: real SMS smoke-test — appears only for the Panacea tile */}
              {selected.key === 'panacea' && selected.configured && (
                <PanaceaTestSmsCard defaultPhone={user?.phone ?? ''} senderId={String(formValues.sender_id ?? '') || undefined} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Panacea: quick "Send test SMS" smoke-test card.
// Appears only on the Panacea integration tile after the config is saved.
// Sends a real SMS via the Panacea gateway to verify credentials +
// sender ID + reachability end-to-end without needing a template/trigger.
// ---------------------------------------------------------------------------
interface PanaceaTestResult {
  ok: boolean;
  provider?: string;
  to?: string;
  body_preview?: string;
  gateway_status?: number | string | null;
  gateway_message?: string | null;
  raw?: unknown;
}

const PanaceaTestSmsCard: React.FC<{ defaultPhone: string; senderId?: string }> = ({ defaultPhone, senderId }) => {
  const [phone, setPhone] = useState<string>(defaultPhone || '');
  const [body, setBody] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [result, setResult] = useState<PanaceaTestResult | null>(null);

  const defaultBody = 'Hello from Osprey UMA — this is a test SMS from your RAPTOR platform.';
  const previewBody = (body || defaultBody).trim();
  const overLimit = previewBody.length > 160;
  const veryOverLimit = previewBody.length > 320;

  const send = async (): Promise<void> => {
    if (!phone.trim()) { toast.error('Enter a recipient phone number.'); return; }
    setSending(true);
    setResult(null);
    try {
      const r = await api.post<PanaceaTestResult>(
        '/integrations/panacea/send-test-sms',
        { to: phone.trim(), body: body.trim() || undefined },
      );
      setResult(r.data);
      if (r.data.ok) toast.success(`SMS accepted by the gateway${r.data.gateway_message ? ` — ${r.data.gateway_message}` : ''}. Check the recipient handset.`);
      else toast.error(`Gateway rejected the message (status ${r.data.gateway_status ?? '?'}): ${r.data.gateway_message ?? 'see raw response.'}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Send failed.';
      toast.error(msg);
      setResult({ ok: false, gateway_message: msg });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-raptor-blue/40" data-testid="panacea-test-sms-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Send size={16} className="text-raptor-blue" /> Send a real test SMS
        </CardTitle>
        <CardDescription>
          Quickly verify Panacea end-to-end without setting up templates or triggers. This sends one real SMS
          using your configured Sender ID{senderId ? ` (${senderId})` : ''}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Recipient phone</Label>
            <Input
              data-testid="panacea-test-phone-input"
              placeholder="+27821234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
            />
            <p className="text-[11px] text-raptor-navy/50 font-body">
              Accepts +27 82 123 4567, 0821234567, or 27821234567 — we normalise to E.164.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label className="flex items-center justify-between">
              <span>Message body <span className="font-normal text-raptor-navy/50">(optional)</span></span>
              <span className={`text-[11px] font-tabular ${veryOverLimit ? 'text-red-600' : overLimit ? 'text-amber-600' : 'text-raptor-navy/50'}`}>
                {previewBody.length} / 160
              </span>
            </Label>
            <textarea
              data-testid="panacea-test-body-input"
              className="min-h-[70px] w-full rounded-md border border-border/60 px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
              placeholder={defaultBody}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            {veryOverLimit && (
              <p className="text-[11px] text-red-600 font-body">Too long — will be truncated to 320 chars.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border/60 pt-3">
          <p className="text-xs text-raptor-navy/60 font-body">
            {overLimit
              ? <>Over 160 chars — the recipient's carrier may split this into <b>{Math.ceil(previewBody.length / 153)}</b> SMS segments.</>
              : <>Under 160 chars — one SMS segment.</>}
          </p>
          <Button
            variant="gradient"
            size="sm"
            onClick={send}
            disabled={sending || !phone.trim() || veryOverLimit}
            data-testid="panacea-send-test-sms-btn"
          >
            <Send size={14} /> {sending ? 'Sending…' : 'Send test SMS'}
          </Button>
        </div>

        {result && (
          <div
            className={`rounded-md border p-3 text-sm font-body ${
              result.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-900'
            }`}
            data-testid="panacea-test-sms-result"
          >
            <div className="flex items-center gap-2 font-semibold">
              {result.ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-red-600" />}
              {result.ok
                ? `SMS accepted by the gateway${result.gateway_message ? ` (${result.gateway_message})` : ''}`
                : 'Gateway rejected the message'}
            </div>
            {result.to && (
              <div className="mt-1 text-xs">Recipient (normalised): <code className="font-tabular">{result.to}</code></div>
            )}
            {result.body_preview && (
              <div className="mt-1 text-xs">Body preview: <span className="italic">{result.body_preview}</span></div>
            )}
            {(result.gateway_status !== undefined || result.gateway_message) && (
              <div className="mt-1 text-xs">
                Gateway: <code className="font-tabular">status {String(result.gateway_status ?? '?')}</code>
                {result.gateway_message ? <> — {result.gateway_message}</> : null}
              </div>
            )}
            {result.raw !== undefined && (
              <details className="mt-2">
                <summary className="text-[11px] cursor-pointer opacity-70">Raw response</summary>
                <pre className="mt-1 text-[11px] font-tabular bg-white/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(result.raw, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
